import React from 'react';
import { Building2, MapPin, Euro, Clock, GraduationCap, ArrowUpRight } from 'lucide-react';
import type { Job } from '../../types/job';
import { cn, formatSalary, formatDate } from '../../lib/utils';
import { SalaryBadge } from '../ui/SalaryBadge';

interface PublicJobCardProps {
  job: Job;
  onClick?: () => void;
}

export function PublicJobCard({ job, onClick }: PublicJobCardProps) {
  const formattedSalary = formatSalary(job);

  return (
    <article 
      onClick={onClick}
      className={cn(
        "group relative bg-white",
        "hover:bg-gray-50/80 rounded-xl",
        "border border-gray-200",
        "transition-all duration-300",
        "hover:shadow-lg hover:-translate-y-0.5",
        "cursor-pointer"
      )}
    >
      {/* New Tag */}
      {job.created_at && new Date(job.created_at).getTime() > Date.now() - 2 * 24 * 60 * 60 * 1000 && (
        <div className="absolute -top-2 -right-2 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full shadow-sm">
          Nouveau
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          {/* Company Logo */}
          {job.business_logo ? (
            <div className={cn(
              "flex-shrink-0 w-12 h-12",
              "rounded-lg overflow-hidden",
              "transition-transform duration-300",
              "group-hover:scale-105"
            )}>
              <img 
                src={job.business_logo} 
                alt={`Logo ${job.business}`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className={cn(
              "flex-shrink-0 w-12 h-12",
              "flex items-center justify-center",
              "rounded-lg bg-teal-50 text-teal-600",
              "text-xl font-bold",
              "transition-transform duration-300",
              "group-hover:scale-105"
            )}>
              {job.business?.charAt(0) || <Building2 className="h-6 w-6" />}
            </div>
          )}

          {/* Title and Company */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-gray-900 truncate group-hover:text-teal-600 transition-colors">
                {job.job_title}
              </h2>
              {/* Contract Type Badge */}
              <span className={cn(
                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                job.contract === 'CDI' ? 'bg-blue-50 text-blue-700' :
                job.contract === 'CDD' ? 'bg-purple-50 text-purple-700' :
                job.contract === 'Stage' ? 'bg-green-50 text-green-700' :
                job.contract === 'Freelance' ? 'bg-yellow-50 text-yellow-700' :
                job.contract === 'Alternance' ? 'bg-orange-50 text-orange-700' :
                job.contract === 'Intérim' ? 'bg-red-50 text-red-700' :
                'bg-gray-50 text-gray-700'
              )}>
                {job.contract}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 truncate">
              {job.business}
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {job.description}
        </p>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center text-gray-500">
            <MapPin className="h-4 w-4 text-gray-400 mr-1.5" />
            <span className="truncate">{job.city || 'Non précisé'}</span>
          </div>

          {formattedSalary && (
            <div className="flex items-center text-gray-500">
              <Euro className="h-4 w-4 text-gray-400 mr-1.5" />
              <span className="truncate font-medium text-teal-700">{formattedSalary}</span>
            </div>
          )}

          {job.required_experience && (
            <div className="flex items-center text-gray-500">
              <GraduationCap className="h-4 w-4 text-gray-400 mr-1.5" />
              <span className="truncate">{job.required_experience}</span>
            </div>
          )}

          {job.created_at && (
            <div className="flex items-center text-gray-500">
              <Clock className="h-4 w-4 text-gray-400 mr-1.5" />
              <span>{formatDate(job.created_at)}</span>
            </div>
          )}
        </div>

        {/* Apply Button with Salary Highlight */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className={cn(
              "flex items-center",
              "text-sm font-medium",
              "text-teal-600 group-hover:text-teal-700",
              "transition-colors"
            )}>
              <span>Voir l'offre</span>
              <ArrowUpRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            
            {formattedSalary && (
              <SalaryBadge 
                salary={formattedSalary} 
                highlighted={true}
                className="text-xs"
              />
            )}
          </div>
        </div>
      </div>
      <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.job_title,
      "description": job.description,
      "datePosted": new Date(job.created_at).toISOString(),
      "validThrough": new Date(new Date(job.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), 
      "employmentType": job.contract || "CDI",
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.business,
        "sameAs": "https://emploi-paysagiste.fr", 
        "logo": job.business_logo || "https://emploi-paysagiste.fr/default-logo.png" // un logo par défaut si absent
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": job.address || "Adresse non précisée",
          "addressLocality": job.city || "Ville non précisée",
          "postalCode": job.zip_code || "00000",
          "addressCountry": "FR"
        }
      }
    })
  }}
/>

    </article>
  );
}